import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useTriggerTrackingServiceAction } from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import { TrackingAction } from '@/types/device'

type CrossCameraServiceActionModalProps = {
  action: TrackingAction
  deviceId: string
  deviceName: string
  latestVersion?: string
  onClose: () => void
  onSuccess?: (action: TrackingAction) => void
}

type ActionConfig = {
  buttonId: string
  confirmationId: string
  buttonColor?: string
}

const ACTION_CONFIG: Record<TrackingAction, ActionConfig> = {
  install: {
    buttonId: 'crossCamera.installation.actions.install',
    confirmationId: 'crossCamera.installation.confirm.install'
  },
  update: {
    buttonId: 'crossCamera.installation.actions.update',
    confirmationId: 'crossCamera.installation.confirm.update'
  },
  start: {
    buttonId: 'crossCamera.installation.actions.start',
    confirmationId: 'crossCamera.installation.confirm.start'
  },
  stop: {
    buttonId: 'crossCamera.installation.actions.stop',
    confirmationId: 'crossCamera.installation.confirm.stop'
  },
  restart: {
    buttonId: 'crossCamera.installation.actions.restart',
    confirmationId: 'crossCamera.installation.confirm.restart'
  },
  uninstall: {
    buttonId: 'crossCamera.installation.actions.uninstall',
    confirmationId: 'crossCamera.installation.confirm.uninstall',
    buttonColor: 'red'
  }
}

export const CrossCameraServiceActionModal = ({
  action,
  deviceId,
  deviceName,
  latestVersion,
  onClose,
  onSuccess
}: CrossCameraServiceActionModalProps) => {
  const intl = useIntl()
  const config = ACTION_CONFIG[action]

  const { mutateAsync: triggerAction, isPending } =
    useTriggerTrackingServiceAction()

  const handleConfirm = async () => {
    try {
      await triggerAction({
        deviceId,
        action,
        version:
          action === 'install' || action === 'update'
            ? latestVersion
            : undefined
      })
      showToast(
        intl.formatMessage({ id: 'crossCamera.installation.requested' }),
        'success'
      )
      onSuccess?.(action)
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'crossCamera.installation.requestFailed' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="crossCamera.installation.service" />}
      opened
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage
            id={config.confirmationId}
            values={{ device: deviceName, version: latestVersion }}
          />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button
            miw={120}
            color={config.buttonColor}
            onClick={() => void handleConfirm()}
          >
            <FormattedMessage id={config.buttonId} />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
